import { useState } from 'react';
import { DATA_SOURCES, type DataSourceId } from '@shared/data-sources';
import { Badge } from '@/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { StatusDot, type StatusTone } from '@/components/StatusBadge';
import {
  summariseFreshness,
  syncedAgo,
  type FreshnessSummary,
  type SourceLine,
} from '@/components/data-freshness/freshness-summary';
import { useDataFreshness } from '@/hooks/use-data-freshness';
import { useMinuteClock } from '@/hooks/use-minute-clock';
import { formatDateTime } from '@/lib/formatters';
import { cn } from '@/lib/utils';

const BADGE_VARIANT: Record<StatusTone, 'muted' | 'success' | 'warning' | 'info' | 'danger'> = {
  // A fresh chip stays quiet: grey with a green dot. Colour is for when something needs a look.
  neutral: 'muted',
  success: 'muted',
  warning: 'warning',
  info: 'info',
  danger: 'danger',
};

function lineText(line: SourceLine, now: number): string {
  const synced =
    line.syncedAt != null
      ? `synced ${formatDateTime(new Date(line.syncedAt))} (${syncedAgo(line.syncedAt, now)})`
      : null;
  switch (line.state) {
    case 'not_connected':
      return 'not connected';
    case 'never':
      return 'connected, not synced yet';
    case 'reconnect':
      return `sign-in expired; an admin can reconnect it in Integrations${synced ? `. Last ${synced}` : ''}`;
    case 'sync_failed':
      return `the last sync failed${synced ? `. Last ${synced}` : ''}`;
    case 'stale':
      return `${synced}, which is over ${staleAfterText(DATA_SOURCES[line.source].staleAfterHours)}`;
    case 'fresh':
      return synced ?? 'synced';
  }
}

/** "36 hours", or days for a monthly source ("32 days", search volumes). */
function staleAfterText(hours: number): string {
  return hours >= 72 && hours % 24 === 0 ? `${hours / 24} days` : `${hours} hours`;
}

/**
 * "Updated 3h ago": how old the synced data on this page is. Grey while fresh, amber once a source
 * is past its `staleAfterHours`, red when its last sync failed or its sign-in expired. The tooltip
 * has a line per source. Pages pass `sources` to PageHeader rather than placing this themselves.
 */
export function DataFreshness({
  sources,
  className,
}: {
  /** The data sources the page shows (DATA_SOURCES ids). */
  sources: readonly DataSourceId[];
  className?: string;
}) {
  const { sources: entries, error } = useDataFreshness();
  const now = useMinuteClock();
  // Nothing until the first answer: the chip is secondary, so it never holds up the page.
  if (!entries && !error) return null;
  return (
    <DataFreshnessChip
      summary={entries ? summariseFreshness(sources, entries, now) : null}
      error={error}
      now={now}
      className={className}
    />
  );
}

/** The chip itself, for a given summary (the /dev/ui gallery shows its states with it). */
export function DataFreshnessChip({
  summary,
  error,
  now,
  className,
}: {
  /** From summariseFreshness; null when the freshness couldn't load. */
  summary: FreshnessSummary | null;
  error?: string | null;
  now: number;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const tone: StatusTone = summary?.tone ?? 'neutral';
  const label = summary?.label ?? 'Freshness unknown';

  return (
    <Tooltip open={open} onOpenChange={setOpen}>
      <TooltipTrigger asChild>
        <Badge
          asChild
          variant={BADGE_VARIANT[tone]}
          className={cn('h-7 cursor-default gap-1.5 px-2.5 font-normal tabular-nums', className)}
        >
          <button
            type="button"
            aria-expanded={open}
            onClick={(e) => {
              // Clicks and taps toggle it too (touch has no hover).
              e.preventDefault();
              setOpen((current) => !current);
            }}
          >
            <StatusDot tone={tone} />
            {label}
          </button>
        </Badge>
      </TooltipTrigger>
      <TooltipContent className="max-w-80 text-left font-normal">
        {summary ? (
          <ul className="space-y-1">
            {summary.lines.map((line) => (
              <li key={line.source}>
                <span className="font-medium">{line.label}</span>: {lineText(line, now)}
              </li>
            ))}
          </ul>
        ) : (
          <p>Couldn't check when the data last synced. {error}</p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
